'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { Briefcase } from 'lucide-react';
import { createClient } from '@/lib/supabase/browser';
import { Card } from '@/components/ds';

interface RecentJob {
  id: string;
  title: string;
  company?: string | null;
  rate?: string | null;
}

export default function RecentJobsWidget() {
  const [jobs, setJobs] = useState<RecentJob[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const supabase = createClient();
      const now = new Date().toISOString();
      const { data } = await supabase
        .from('job_postings')
        .select('id, title, company, rate')
        .eq('status', 'published')
        .eq('is_archived', false)
        .eq('is_filled', false)
        .or(`post_removal_date.is.null,post_removal_date.gt.${now}`)
        .order('created_at', { ascending: false })
        .limit(5);
      setJobs(data || []);
      setLoading(false);
    };
    load();
  }, []);

  return (
    <Card>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1.5">
          <Briefcase className="w-4 h-4 text-accent" strokeWidth={1.75} />
          <p className="text-text-primary font-semibold text-sm">Recent Jobs</p>
        </div>
        <Link
          href="/jobs"
          className="text-accent text-xs hover:text-accent-hover transition-colors"
        >
          View all →
        </Link>
      </div>
      <p className="text-text-secondary text-xs mb-3">
        Latest opportunities from verified posters
      </p>

      {loading ? (
        <div className="py-3 flex justify-center">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-accent" />
        </div>
      ) : jobs.length === 0 ? (
        <p className="text-text-tertiary text-xs">No open jobs right now.</p>
      ) : (
        <div className="space-y-1.5">
          {jobs.map((job) => (
            <Link key={job.id} href={`/jobs/${job.id}`}>
              <div className="p-2.5 rounded-sm bg-surface-3 hover:bg-surface-2 transition-colors">
                <p className="text-text-primary text-sm font-medium line-clamp-1">
                  {job.title}
                </p>
                {job.company && (
                  <p className="text-text-secondary text-xs line-clamp-1 mt-0.5">
                    {job.company}
                  </p>
                )}
                {job.rate && (
                  <p className="text-text-tertiary text-xs mt-0.5">{job.rate}</p>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </Card>
  );
}
